import { MAX_PROCESS_SERVICES, USER_ROLE } from "../constants";
import {
  ITINERARY_STATUS,
  ITINERARY_PUBLIC_STATUS,
  ITINERARY_TYPES,
  QUOTES_STATUS,
  REQUEST_STAGES,
} from "../constants/itinerary";

import globalHelper from "@/helpers";
import helpers from "@/helpers";

export const getLocationsPath = (locations = []) => {
  const newLocationPath = locations.reduce((acc, location, ind) => {
    if (!location.id) return acc;
    const pathSign = ind === locations.length - 1 ? "" : ",/";
    return `${acc}${location.location_code}${pathSign}`;
  }, "");

  return newLocationPath;
};

const getLocationString = (location = []) => {
  return location.reduce((acc, location) => {
    return `${acc} ${location.location_name}`;
  }, "");
};

const getServiceSearchString = (service = {}) => {
  return `${service.code} ${service.name.value} ${
    service.level_text
  } ${getLocationString(service.location)}`;
};

const setPageTitle = (title = "") => {
  document.title = title;
};

const resetPageTitle = () => {
  const title = process.env.VUE_APP_TITLE;
  setPageTitle(title);
};

const getItineraryTypesMapped = () => {
  return Object.keys(ITINERARY_TYPES).map((key) => ({
    id: ITINERARY_TYPES[key].id,
    name: ITINERARY_TYPES[key].name,
    type: ITINERARY_TYPES[key].type,
  }));
};

const getQuotePublicStatusesMapped = () => {
  return Object.keys(ITINERARY_PUBLIC_STATUS).map((key) => ({
    id: ITINERARY_PUBLIC_STATUS[key].id,
    name: ITINERARY_PUBLIC_STATUS[key].name,
  }));
};

const getItineraryTypeById = (itineraryTypeId = "") => {
  const itineraryTypes = getItineraryTypesMapped();
  return itineraryTypes.find(
    (itineraryType) => itineraryType.id === itineraryTypeId
  );
};

const getQuoteStatusPhases = (quoteStatus = "", quoteStatuses = []) => {
  const defaultQuoteStatuses = [
    {
      id: ITINERARY_STATUS.QUOTE.value,
      name: "In Preparation",
      completed: false,
    },
    {
      id: ITINERARY_STATUS.SENT.value,
      name: "Sent",
      completed: false,
    },
    {
      id: ITINERARY_STATUS.CONFIRM.value,
      name: "Confirmed",
      completed: false,
    },
  ];

  const quoteStatusesPhases = quoteStatuses.length
    ? quoteStatuses
    : defaultQuoteStatuses;

  const filtreredQuoteStatusesPhases = quoteStatusesPhases.filter(
    (status) =>
      ![
        ITINERARY_STATUS.CANCEL.value,
        ITINERARY_STATUS.UNPUBLISHED.value,
      ].includes(status.id)
  );

  const currentQuoteStatusIndex = filtreredQuoteStatusesPhases.findIndex(
    (status) => status.id === quoteStatus
  );

  return filtreredQuoteStatusesPhases.map((status, index) => {
    const isConfirmed = quoteStatus === ITINERARY_STATUS.CONFIRM.value;
    return {
      ...status,
      completed: index < currentQuoteStatusIndex || isConfirmed,
      active: index <= currentQuoteStatusIndex,
    };
  });
};

const getRoomPreferableDistributionHash = (roomPreferableDistJson) => {
  const sortedRoomPreferableDistributionJson = Object.values(
    roomPreferableDistJson.reduce((acc, item) => {
      const currentItem = acc[item.id_room_type];

      if (!currentItem || item.entity_id > currentItem.entity_id) {
        acc[item.id_room_type] = item;
      }

      return acc;
    }, {})
  ).sort((a, b) => a.id_room_type - b.id_room_type);

  const currentRoomDistribution = sortedRoomPreferableDistributionJson.reduce(
    (acc, item) => {
      if (item.qty === 0) return acc;
      return acc + `${item.id_room_type}-${item.qty}`;
    },
    ""
  );

  return currentRoomDistribution;
};

const getItineraryRowClass = (row = {}) => {
  if (row.isDeleting) return "itinerary__row is-deleting";
  if (row.isExporting) return "itinerary__row is-exporting";
  if (row.isUpdating) return "itinerary__row is-updating";
  return "itinerary__row";
};

const generateList = (start, end) => {
  return Array.from({ length: end - start + 1 }, (_, index) => ({
    value: start + index,
  }));
};

const getFullRequestStage = (requestStage) => {
  const requestStages = Object.values(REQUEST_STAGES);
  return requestStages.find((stage) => stage.id === requestStage);
};

const getFullQuoteStatus = (quoteStatus) => {
  const quoteStatuses = Object.values(QUOTES_STATUS);
  return quoteStatuses.find((status) => status.id === quoteStatus);
};

const getServicesChunks = (services) => {
  const serviceUUIDs = services.map((service) => service.id);

  return globalHelper.sliceIntoChunks(serviceUUIDs, MAX_PROCESS_SERVICES);
};

const hasStatus = (quoteStatus, statuses) => {
  return statuses.some((s) => s === quoteStatus);
};

const getCountryByDestination = (destination, contractLocations) => {
  return contractLocations?.find(
    (location) => location.id === destination.parent
  );
};

const getLocationOnMount = (location, contractLocations) => {
  const validatedLocation = location || [];

  const isFirstServiceLocationValid =
    validatedLocation && validatedLocation.length > 0;

  if (!isFirstServiceLocationValid) return;

  const hasCountryLocation = validatedLocation.some((location) =>
    [null, undefined].includes(location.parent)
  );

  const firstServiceCountry = hasCountryLocation
    ? validatedLocation[0]
    : getCountryByDestination(validatedLocation[0], contractLocations);

  return {
    country: firstServiceCountry,
    destination: validatedLocation?.[0],
  };
};

export const helper = {
  getLocationsPath,
  getLocationString,
  getServiceSearchString,
  setPageTitle,
  resetPageTitle,
  getItineraryTypesMapped,
  getItineraryTypeById,
  getQuoteStatusPhases,
  getRoomPreferableDistributionHash,
  getQuotePublicStatusesMapped,
  getItineraryRowClass,
  generateList,
  getFullRequestStage,
  getFullQuoteStatus,
  getServicesChunks,
  hasStatus,
  getLocationOnMount,
  filterVisibleServices(services = [], authData) {
    return services.filter((service) => {
      const isTravelAgent = helpers.hasAnyRoles(
        authData.user,
        [USER_ROLE.TRAVEL_AGENT],
        false
      );

      return isTravelAgent ? service.visible : true;
    });
  },
  getTotalPrice(services = []) {
    return parseFloat(services.reduce((a, b) => a + b.price, 0));
  },
  getTotalCost(services = []) {
    return parseFloat(services.reduce((a, b) => a + b.cost_cur, 0));
  },
  getTotalMargin(services = []) {
    return parseFloat(services.reduce((a, b) => a + b.margin, 0));
  },
  getTotalPteMargin(services = []) {
    const price = this.getTotalPrice(services);
    const cost = this.getTotalCost(services);
    return (price ? (100 * (price - cost)) / price : 0).toFixed(2);
  },
  getMarginClass(value) {
    if (value <= 0) return "";
    if (value < 14) return "margin_bad";
    if (value < 20) return "margin_warning";
    return "margin_good";
  },
};
